// packages/utils/api-client/src/lib/apiClient.ts

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError
} from 'axios';
import { addTokenRefreshInterceptor, type SessionRefreshOptions } from './tokenRefreshInterceptor';
import { viewerTimeZoneHeaders } from './viewerTimeZone';
import { activeOrganizationHeaders } from './activeOrganization';

export interface ApiClientConfig {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  retryCondition?: (error: AxiosError) => boolean;
  tokenProvider?: () => Promise<string | undefined>;
  /**
   * Renews the signed-in session when a request is answered 401; the request is then
   * replayed once with a fresh token from `tokenProvider`. Omitted, a 401 is surfaced as-is.
   * See `addTokenRefreshInterceptor`.
   */
  refreshSession?: SessionRefreshOptions['refreshSession'];

  /**
   * Optional observer callbacks invoked for every successful / failed
   * response from this client. Intended for analytics / telemetry —
   * keep the implementations side-effect-free and synchronous; throwing
   * from them does not affect the request outcome (the client swallows
   * any errors so observability never breaks the API call).
   */
  onResponse?: (response: AxiosResponse) => void;
  onError?: (error: AxiosError) => void;
}

/**
 * Extended request config that allows per-request retry override
 */
export interface ApiRequestConfig extends AxiosRequestConfig {
  /** Override the default number of retries for this request (0 = no retries) */
  retries?: number;
}

export interface ApiClient {
  get<T = any>(
    url: string,
    config?: ApiRequestConfig,
    authRequired?: boolean
  ): Promise<T>;

  post<T = any>(
    url: string,
    data?: any,
    config?: ApiRequestConfig,
    authRequired?: boolean
  ): Promise<T>;

  put<T = any>(
    url: string,
    data?: any,
    config?: ApiRequestConfig,
    authRequired?: boolean
  ): Promise<T>;

  patch<T = any>(
    url: string,
    data?: any,
    config?: ApiRequestConfig,
    authRequired?: boolean
  ): Promise<T>;

  delete<T = any>(
    url: string,
    config?: ApiRequestConfig,
    authRequired?: boolean
  ): Promise<T>;
}

/**
 * Default retry condition - retry on network errors and 5xx/429 status codes
 */
const defaultRetryCondition = (error: AxiosError): boolean => {
  if (!error.response) {
    // Network error, timeout, etc.
    return true;
  }
  
  const retryableCodes = [408, 429, 500, 502, 503, 504];
  return retryableCodes.includes(error.response.status);
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number): Promise<void> => 
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay
 */
const calculateRetryDelay = (attempt: number, baseDelay: number): number => {
  return Math.min(baseDelay * Math.pow(2, attempt), 10000); // Cap at 10 seconds
};

/**
 * Attaches the viewer's IANA time zone (`x-center-timezone`) to every request
 * this instance issues.
 *
 * A REQUEST INTERCEPTOR rather than an `axios.create({ headers })` default,
 * for two reasons. The zone is read per request, so a viewer who changes zone
 * mid-session (a laptop that has travelled, an OS setting changed in another
 * tab) is followed without the client being rebuilt. And it applies uniformly
 * to every verb and to unauthenticated calls on this client — the zone is a
 * property of the CALLER, not of the credential, and `withAuth` (which every
 * verb funnels through) is skipped entirely when `authRequired` is false.
 *
 * The header is omitted, never guessed, when the runtime cannot name a zone —
 * see `resolveViewerTimeZone`. The server then applies its documented UTC
 * fallback (`resolveEffectiveTimeZone`).
 *
 * Not applied to `createPublicApiClient`: nothing reachable without a token
 * resolves a filter variable, so the zone would be a fingerprint sent to
 * anonymous endpoints for no behaviour.
 */
function addViewerTimeZoneHeader(http: AxiosInstance): void {
  http.interceptors.request.use((config) => {
    // `config.headers` is an `AxiosHeaders` instance on an interceptor's
    // `InternalAxiosRequestConfig`, not a plain object, so it is mutated
    // through `set()` rather than reassigned — a plain-object assignment
    // drops the instance methods the adapter later calls.
    for (const [name, value] of Object.entries(viewerTimeZoneHeaders())) {
      config.headers.set(name, value);
    }
    return config;
  });
}

/**
 * Attaches the tab's active organization (`x-center-organization-id`) to every
 * request this instance issues. Read per request from the in-memory store, so a
 * switch is followed without rebuilding the client; omitted in metasphere mode.
 * The server validates it against graph membership; see `activeOrganization.ts`.
 *
 * Not applied to `createPublicApiClient`: anonymous endpoints have no organization.
 */
function addActiveOrganizationHeader(http: AxiosInstance): void {
  http.interceptors.request.use((config) => {
    for (const [name, value] of Object.entries(activeOrganizationHeaders())) {
      config.headers.set(name, value);
    }
    return config;
  });
}

/**
 * Factory for a token-less API client that never attaches an Authorization header.
 * Use exclusively for public, unauthenticated endpoints.
 *
 * Accepts (and ignores) the `authRequired` parameter on each method to satisfy the
 * ApiClient interface — but the public client is always unauthenticated regardless.
 */
export function createPublicApiClient(
  baseURL: string,
  config: Pick<
    ApiClientConfig,
    'timeout' | 'retries' | 'retryDelay' | 'retryCondition' | 'onResponse' | 'onError'
  > = {}
): ApiClient {
  if (typeof baseURL !== 'string' || !baseURL.trim()) {
    throw new Error('createPublicApiClient: baseURL must be a non-empty string');
  }

  const {
    timeout = 30000,
    retries = 3,
    retryDelay = 1000,
    retryCondition = defaultRetryCondition,
    onResponse,
    onError,
  } = config;

  const http: AxiosInstance = axios.create({ baseURL, timeout });

  if (onResponse || onError) {
    http.interceptors.response.use(
      (response) => {
        if (onResponse) {
          try {
            onResponse(response);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[PublicApiClient] onResponse observer threw', err);
          }
        }
        return response;
      },
      (error: AxiosError) => {
        if (onError) {
          try {
            onError(error);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[PublicApiClient] onError observer threw', err);
          }
        }
        throw error;
      }
    );
  }

  const extract = <R>(res: AxiosResponse<R>): R => {
    if (res.status === 204) return undefined as R;
    if (!res?.data) throw new Error('Response invalid or missing data');
    return res.data;
  };

  async function executeWithRetry<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    maxRetries = retries
  ): Promise<T> {
    let lastError: AxiosError | Error;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await requestFn();
        return extract(response);
      } catch (error: any) {
        lastError = error;
        if (attempt === maxRetries) break;
        if (!retryCondition(error as AxiosError)) break;
        const delay = calculateRetryDelay(attempt, retryDelay);
        await sleep(delay);
      }
    }
    throw lastError!;
  }

  return {
    get: async <T = any>(
      url: string,
      cfg?: ApiRequestConfig,
      _authRequired?: boolean
    ): Promise<T> => {
      if (typeof url !== 'string' || !url.trim()) {
        throw new Error('API get: URL must be non-empty');
      }
      return executeWithRetry(
        () => http.get<T>(url, cfg ?? {}),
        cfg?.retries ?? retries
      );
    },

    post: async <T = any>(
      url: string,
      data?: any,
      cfg?: ApiRequestConfig,
      _authRequired?: boolean
    ): Promise<T> => {
      if (typeof url !== 'string' || !url.trim()) {
        throw new Error('API post: URL must be non-empty');
      }
      // POST is non-idempotent — never auto-retry. A timed-out/5xx create may
      // have already succeeded server-side; retrying it duplicates the resource
      // (e.g. multiple orgs from one "Add Organization"). Opt in with {retries}
      // only for POSTs you know are idempotent.
      return executeWithRetry(
        () => http.post<T>(url, data, cfg ?? {}),
        cfg?.retries ?? 0
      );
    },

    put: async <T = any>(
      url: string,
      data?: any,
      cfg?: ApiRequestConfig,
      _authRequired?: boolean
    ): Promise<T> => {
      if (typeof url !== 'string' || !url.trim()) {
        throw new Error('API put: URL must be non-empty');
      }
      return executeWithRetry(
        () => http.put<T>(url, data, cfg ?? {}),
        cfg?.retries ?? retries
      );
    },

    patch: async <T = any>(
      url: string,
      data?: any,
      cfg?: ApiRequestConfig,
      _authRequired?: boolean
    ): Promise<T> => {
      if (typeof url !== 'string' || !url.trim()) {
        throw new Error('API patch: URL must be non-empty');
      }
      // PATCH is non-idempotent — never auto-retry by default (opt in with {retries}).
      return executeWithRetry(
        () => http.patch<T>(url, data, cfg ?? {}),
        cfg?.retries ?? 0
      );
    },

    delete: async <T = any>(
      url: string,
      cfg?: ApiRequestConfig,
      _authRequired?: boolean
    ): Promise<T> => {
      if (typeof url !== 'string' || !url.trim()) {
        throw new Error('API delete: URL must be non-empty');
      }
      return executeWithRetry(
        () => http.delete<T>(url, cfg ?? {}),
        cfg?.retries ?? retries
      );
    },
  };
}

/**
 * Factory for creating an API client with auth per-request and retry logic.
 *
 * `tokenProvider` is the only token source — every real caller supplies one
 * (design-system's `ApiClientsProvider` always passes impersonation-aware
 * `getEffectiveToken`), so this module carries no auth-stack-specific
 * fallback (EAINT-954 Phase 4: shared/ knows about no auth stack at all).
 */
export function createApiClient(
  baseURL: string,
  config: ApiClientConfig & { tokenProvider: () => Promise<string | undefined> }
): ApiClient {
  if (typeof baseURL !== 'string' || !baseURL.trim()) {
    throw new Error('createApiClient: baseURL must be a non-empty string');
  }

  const {
    timeout = 30000,
    retries = 3,
    retryDelay = 1000,
    retryCondition = defaultRetryCondition,
    tokenProvider,
    refreshSession,
    onResponse,
    onError,
  } = config;

  const http: AxiosInstance = axios.create({ baseURL, timeout });

  addViewerTimeZoneHeader(http);
  addActiveOrganizationHeader(http);

  if (refreshSession) {
    addTokenRefreshInterceptor(http, { refreshSession, tokenProvider });
  }

  // Telemetry observer interceptor — called after session renewal so it
  // sees final outcomes only. Wrapped in try/catch so a misbehaving
  // observer never breaks an API call.
  if (onResponse || onError) {
    http.interceptors.response.use(
      (response) => {
        if (onResponse) {
          try {
            onResponse(response);
          } catch (err) {
            // Observers must not throw. Swallow.
            // eslint-disable-next-line no-console
            console.warn('[ApiClient] onResponse observer threw', err);
          }
        }
        return response;
      },
      (error: AxiosError) => {
        if (onError) {
          try {
            onError(error);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[ApiClient] onError observer threw', err);
          }
        }
        throw error;
      }
    );
  }

  async function withAuth(
    config: ApiRequestConfig = {},
    authRequired = true
  ): Promise<ApiRequestConfig> {
    if (!authRequired) {
      return config;
    }
    config.headers ??= {};

    const token = await tokenProvider();

    if (token) {
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return config;
  }

  const extract = <R>(res: AxiosResponse<R>): R => {
    // Handle 204 No Content responses - they're valid but have no data
    if (res.status === 204) {
      return undefined as R;
    }
    
    if (!res?.data) {
      throw new Error('Response invalid or missing data');
    }
    return res.data;
  };

  /**
   * Execute request with retry logic
   */
  async function executeWithRetry<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    maxRetries = retries
  ): Promise<T> {
    let lastError: AxiosError | Error;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await requestFn();
        return extract(response);
      } catch (error: any) {
        lastError = error;
        
        // Don't retry on the last attempt
        if (attempt === maxRetries) {
          break;
        }
        
        // Check if error is retryable
        if (!retryCondition(error as AxiosError)) {
          break;
        }
        
        // Wait before retrying
        const delay = calculateRetryDelay(attempt, retryDelay);
        console.warn(`API request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms:`, error.message);
        await sleep(delay);
      }
    }
    
    throw lastError!;
  }

  return {
    get: async <T = any>(
      url: string,
      cfg?: ApiRequestConfig,
      auth = true
    ): Promise<T> => {
      if (typeof url !== 'string' || !url.trim()) {
        throw new Error('API get: URL must be non-empty');
      }

      // Extract per-request retries override
      const requestRetries = cfg?.retries ?? retries;

      return executeWithRetry(async () => {
        const finalCfg = await withAuth(cfg, auth);
        return http.get<T>(url, finalCfg);
      }, requestRetries);
    },

    post: async <T = any>(
      url: string,
      data?: any,
      cfg?: ApiRequestConfig,
      auth = true
    ): Promise<T> => {
      if (typeof url !== 'string' || !url.trim()) {
        throw new Error('API post: URL must be non-empty');
      }

      // POST is non-idempotent — never auto-retry. A timed-out/5xx create may
      // have already succeeded server-side; retrying it duplicates the resource
      // (e.g. multiple orgs from one "Add Organization"). Opt in with {retries}
      // only for POSTs you know are idempotent.
      const requestRetries = cfg?.retries ?? 0;

      return executeWithRetry(async () => {
        const finalCfg = await withAuth(cfg, auth);
        return http.post<T>(url, data, finalCfg);
      }, requestRetries);
    },

    put: async <T = any>(
      url: string,
      data?: any,
      cfg?: ApiRequestConfig,
      auth = true
    ): Promise<T> => {
      if (typeof url !== 'string' || !url.trim()) {
        throw new Error('API put: URL must be non-empty');
      }

      const requestRetries = cfg?.retries ?? retries;

      return executeWithRetry(async () => {
        const finalCfg = await withAuth(cfg, auth);
        return http.put<T>(url, data, finalCfg);
      }, requestRetries);
    },

    patch: async <T = any>(
      url: string,
      data?: any,
      cfg?: ApiRequestConfig,
      auth = true
    ): Promise<T> => {
      if (typeof url !== 'string' || !url.trim()) {
        throw new Error('API patch: URL must be non-empty');
      }

      // PATCH is non-idempotent — never auto-retry by default (opt in with {retries}).
      const requestRetries = cfg?.retries ?? 0;

      return executeWithRetry(async () => {
        const finalCfg = await withAuth(cfg, auth);
        return http.patch<T>(url, data, finalCfg);
      }, requestRetries);
    },

    delete: async <T = any>(
      url: string,
      cfg?: ApiRequestConfig,
      auth = true
    ): Promise<T> => {
      if (typeof url !== 'string' || !url.trim()) {
        throw new Error('API delete: URL must be non-empty');
      }

      const requestRetries = cfg?.retries ?? retries;

      return executeWithRetry(async () => {
        const finalCfg = await withAuth(cfg, auth);
        return http.delete<T>(url, finalCfg);
      }, requestRetries);
    }
  };
}